import { auth, db, storage } from "@/firebase/config";
import {
  CollectionReference,
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  limit,
  orderBy,
  query,
  updateDoc,
} from "@firebase/firestore";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogContentFloating,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useEffect, useRef, useState } from "react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { z } from "zod";
import { ref, uploadBytes, getDownloadURL, getBlob } from "firebase/storage";
import { Form, FormControl, FormField, FormItem, FormMessage } from "./ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { toast } from "./ui/use-toast";
import { Loader2, X } from "lucide-react";
import { useAuthState } from "react-firebase-hooks/auth";
import {
  type CarouselApi,
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "./ui/carousel";
import { Card, CardContent } from "./ui/card";
import { motion } from "motion/react";

const preloadedImageUrls = new Set<string>();
const NORMALIZED_IMAGE_MAX_DIMENSION = 1200;
const NORMALIZED_IMAGE_TYPE = "image/jpeg";
const NORMALIZED_IMAGE_QUALITY = 0.9;

const createStoragePath = (prefix = "post-images") => {
  const randomId =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `${prefix}/${randomId}.jpg`;
};

const loadImageElementFromBlob = (blob: Blob) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();

    image.onload = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(image);
    };

    image.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      reject(new Error("Unable to load image blob."));
    };

    image.src = objectUrl;
  });

const normalizeImageBlob = async (blob: Blob) => {
  const sourceImage = await loadImageElementFromBlob(blob);

  const width = sourceImage.naturalWidth || sourceImage.width;
  const height = sourceImage.naturalHeight || sourceImage.height;

  if (!width || !height) {
    throw new Error("Image dimensions could not be determined.");
  }

  const scaleFactor = Math.min(1, NORMALIZED_IMAGE_MAX_DIMENSION / Math.max(width, height));
  const targetWidth = Math.max(1, Math.round(width * scaleFactor));
  const targetHeight = Math.max(1, Math.round(height * scaleFactor));

  const canvas = document.createElement("canvas");
  canvas.width = targetWidth;
  canvas.height = targetHeight;

  const context = canvas.getContext("2d", { alpha: false });
  if (!context) {
    throw new Error("Could not create image processing context.");
  }

  context.drawImage(sourceImage, 0, 0, targetWidth, targetHeight);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (normalizedBlob) => {
        if (!normalizedBlob) {
          reject(new Error("Image normalization failed."));
          return;
        }
        resolve(normalizedBlob);
      },
      NORMALIZED_IMAGE_TYPE,
      NORMALIZED_IMAGE_QUALITY,
    );
  });
};

const preloadImage = (url?: string) => {
  if (!url || preloadedImageUrls.has(url)) return;

  const img = new Image();

  const markLoaded = () => {
    img.onload = null;
    img.onerror = null;
    preloadedImageUrls.add(url);
  };

  img.onload = () => {
    if (typeof img.decode === "function") {
      img
        .decode()
        .catch(() => {
          // Some browsers reject decode for otherwise valid images after load.
        })
        .finally(markLoaded);
      return;
    }

    markLoaded();
  };

  img.onerror = () => {
    img.onload = null;
    img.onerror = null;
  };

  img.src = url;
};

const PostItem = ({
  title,
  body,
  imageURL,
  timestamp,
  id,
  setDocs,
  allPosts,
  initialIndex,
  mainCarouselApi,
}: {
  title: string;
  body: string;
  imageURL: string;
  timestamp: { seconds: number };
  id: string;
  setDocs: React.Dispatch<
    React.SetStateAction<
      (Post & {
        id: string;
      })[]
    >
  >;
  allPosts: (Post & { id: string })[];
  initialIndex: number;
  mainCarouselApi?: CarouselApi;
}) => {
  const [user] = useAuthState(auth);
  const loggedIn = !!user;

  const [isLoading, setIsLoading] = useState(false);
  const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isOpen, setIsOpen] = useState(false);
  const [thumbnailCarouselApi, setThumbnailCarouselApi] = useState<CarouselApi>();
  const [thumbnailInitialIndex, setThumbnailInitialIndex] = useState(initialIndex);
  const [thumbnailCarouselKey, setThumbnailCarouselKey] = useState(0);
  const skipInitialThumbnailSyncRef = useRef(false);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);

    if (open) {
      // Start each open with a fresh thumbnail carousel at the clicked post.
      setCurrentIndex(initialIndex);
      setThumbnailInitialIndex(initialIndex);
      setThumbnailCarouselKey((prev) => prev + 1);
      skipInitialThumbnailSyncRef.current = true;
    }
  };

  // Update current index when initial index changes (e.g., when posts are deleted)
  useEffect(() => {
    setCurrentIndex(initialIndex);
  }, [initialIndex]);

  const currentPost = allPosts[currentIndex] ?? allPosts[0];

  useEffect(() => {
    if (!isOpen) return;

    if (allPosts.length === 0) {
      setIsOpen(false);
      return;
    }

    if (currentIndex >= allPosts.length) {
      setCurrentIndex(allPosts.length - 1);
    }
  }, [allPosts.length, currentIndex, isOpen]);

  const removePost = () => {
    if (!currentPost) return;

    setIsLoading(true);

    const docRef = doc(db, `posts/${currentPost.id}`);

    deleteDoc(docRef)
      .then(() => {
        setDocs((prev) => prev.filter((doc) => doc.id !== currentPost.id));
        // Close dialog after deletion
        setIsOpen(false);
      })
      .catch((err) => {
        console.log(err);
        setIsLoading(false);
      });
  };

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : allPosts.length - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev < allPosts.length - 1 ? prev + 1 : 0));
  };

  // Auto-scroll thumbnail carousel to center selected item
  useEffect(() => {
    if (!thumbnailCarouselApi || !isOpen) return;

    if (skipInitialThumbnailSyncRef.current) {
      skipInitialThumbnailSyncRef.current = false;
      return;
    }

    thumbnailCarouselApi.scrollTo(currentIndex);
  }, [currentIndex, thumbnailCarouselApi, isOpen]);

  // Preload current + adjacent images while dialog is open for instant navigation.
  useEffect(() => {
    if (!isOpen || allPosts.length === 0) return;

    const current = allPosts[currentIndex];
    const next = allPosts[(currentIndex + 1) % allPosts.length];
    const prev = allPosts[(currentIndex - 1 + allPosts.length) % allPosts.length];

    preloadImage(current?.imageURL);
    preloadImage(next?.imageURL);
    preloadImage(prev?.imageURL);
  }, [isOpen, currentIndex, allPosts]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        goToPrevious();
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        goToNext();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, currentIndex, allPosts.length]);

  // Sync dialog navigation to main carousel
  useEffect(() => {
    if (!isOpen || !mainCarouselApi) return;

    // When navigating in dialog, update main carousel with smooth animation
    mainCarouselApi.scrollTo(currentIndex);
  }, [currentIndex, isOpen, mainCarouselApi]);

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger className="outline-none w-full">
        <div className="h-[300px] snap-center hover:scale-[1.02] duration-100 hover:cursor-pointer relative post-item rounded-lg overflow-hidden">
          <img
            src={imageURL}
            alt={title}
            className="block w-full h-full object-cover transform-gpu"
            loading="eager"
            decoding="async"
          />
          <div className="absolute w-full bottom-0 h-1/2 bg-gradient-to-b from-[#00000000] to-[#000000ff]" />
          {title && body && (
            <div className="absolute w-full bottom-5 px-5">
              <div className="text-card text-xl">{title}</div>
              <div className="text-[#ddd] text-sm font-light leading-5  hidden md:line-clamp-2">{body}</div>
            </div>
          )}
        </div>
      </DialogTrigger>
      <DialogContentFloating>
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.32, ease: "easeOut" }}
          className="pointer-events-auto flex flex-col items-center gap-3 py-6"
        >
          {loggedIn &&
            currentPost &&
            (!isLoading ? (
              isConfirmingDelete ? (
                <div className="mt-5 flex items-center gap-2">
                  <Button variant="outline" onClick={() => setIsConfirmingDelete(false)}>
                    Cancel
                  </Button>
                  <Button variant="destructive" onClick={removePost}>
                    Confirm Delete
                  </Button>
                </div>
              ) : (
                <Button variant="destructive" className="mt-5" onClick={() => setIsConfirmingDelete(true)}>
                  Delete Post
                </Button>
              )
            ) : (
              <Button disabled className="mt-5" variant="destructive">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Deleting
              </Button>
            ))}
          {/* Main image - floating */}
          {currentPost && (
            <div className="relative w-[850px] max-w-[95vw] aspect-[12/9] min-h-[200px] rounded-xl overflow-hidden shadow-2xl pointer-events-auto shrink-0">
              <img
                src={currentPost.imageURL}
                alt={currentPost.title}
                className="block w-full h-full object-cover bg-muted/50 transform-gpu"
                loading="eager"
                decoding="async"
                fetchPriority="high"
              />
              {/* Close button */}
              <DialogClose className="absolute right-4 top-4 rounded-sm bg-black/50 hover:bg-black/70 text-white p-2 transition-opacity">
                <X className="h-4 w-4" />
              </DialogClose>
              {/* Title overlay with black gradient */}
              {currentPost.title && (
                <div className="absolute inset-0 flex items-end justify-center pointer-events-none">
                  <div className="w-full bg-gradient-to-t from-black/80 via-black/40 to-transparent p-5">
                    <h2 className="text-white text-2xl font-bold text-center">{currentPost.title}</h2>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Text content - third floating element */}

          {/* Thumbnail strip - separate floating box */}
          {allPosts.length > 1 && (
            <div className="relative w-[850px] max-w-[95vw] md:max-w-[850px] pointer-events-auto">
              <div className="bg-background/95 backdrop-blur-sm rounded-xl shadow-lg border border-border/50 md:p-2 p-1">
                {/* Fade gradient - left */}
                <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-background/95 to-transparent z-10 pointer-events-none rounded-l-xl" />

                {/* Thumbnail carousel */}
                <Carousel
                  key={thumbnailCarouselKey}
                  opts={{
                    loop: true,
                    align: "center",
                    dragFree: false,
                    containScroll: "trimSnaps",
                    skipSnaps: false,
                    startIndex: thumbnailInitialIndex,
                  }}
                  setApi={setThumbnailCarouselApi}
                  className="w-full"
                >
                  <CarouselContent className="-ml-2 py-1 md:py-2">
                    {allPosts.map((post, index) => (
                      <CarouselItem
                        key={post.id}
                        className={`md:basis-[23%] sm:basis-[36%] basis-[55%] shrink-0 px-px sm:px-1 md:px-2 noselect  
                      `}
                        onClick={() => setCurrentIndex(index)}
                      >
                        <motion.div
                          className={`lg:h-[100px] md:h-[120px] h-[110px] snap-center relative rounded-lg overflow-hidden transition-all duration-100 ${
                            index === currentIndex
                              ? "opacity-100"
                              : "opacity-75 hover:-translate-y-1 hover:cursor-pointer"
                          }`}
                          style={{
                            boxShadow: "0 2px 6px rgba(0,0,0,0.2)",
                          }}
                        >
                          <img
                            src={post.imageURL}
                            alt={title}
                            className="block w-full h-full object-cover transform-gpu"
                            loading="lazy"
                            decoding="async"
                          />
                          <div className="absolute w-full bottom-0 h-1/2 bg-gradient-to-b from-[#00000000] to-[#000000ff]" />
                        </motion.div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                </Carousel>

                {/* Fade gradient - right */}
                <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-background/95 to-transparent z-10 pointer-events-none rounded-r-xl" />
              </div>
            </div>
          )}

          {currentPost?.body && (
            <div className="w-[850px] max-w-[95vw] bg-background/95 backdrop-blur-sm rounded-xl shadow-lg border border-border/50 p-6 pointer-events-auto">
              <div className="text-center tracking-tight leading-snug">{currentPost.body}</div>
            </div>
          )}
        </motion.div>
      </DialogContentFloating>
    </Dialog>
  );
};

const NewPost = ({
  setDocs,
}: {
  setDocs: React.Dispatch<
    React.SetStateAction<
      (Post & {
        id: string;
      })[]
    >
  >;
}) => {
  const FormSchema = z.object({
    title: z.string().optional(),
    body: z.string().optional(),
    image: z.any().refine((file) => file !== undefined, "Image is required."),
    // .refine((files) => files?.[0]?.size <= MAX_FILE_SIZE, `Max file size is 5MB.`)
    // .refine(
    //   (files) => ACCEPTED_IMAGE_TYPES.includes(files?.[0]?.type),
    //   ".jpg, .jpeg, .png and .webp files are accepted."
    // ),
  });

  const closeRef = useRef(null);

  const [isLoading, setIsLoading] = useState(false);

  const form = useForm<z.infer<typeof FormSchema>>({
    resolver: zodResolver(FormSchema),
  });

  const onSubmit = async (data: z.infer<typeof FormSchema>) => {
    setIsLoading(true);
    try {
      const normalizedImage = await normalizeImageBlob(data.image as Blob);
      const storageRef = ref(storage, createStoragePath());
      const uploadTask = await uploadBytes(storageRef, normalizedImage, {
        contentType: NORMALIZED_IMAGE_TYPE,
        cacheControl: "public,max-age=31536000,immutable",
      });
      const url = await getDownloadURL(uploadTask.ref);

      const newData = {
        title: data.title ?? "",
        body: data.body ?? "",
        imageURL: url,
        timestamp: Timestamp.now(),
      };

      addDoc(postsRef, newData)
        .then((value) => {
          toast({
            title: "Success!",
            description: <div>Your new post is up and running!</div>,
          });
          setDocs((prev) => [{ ...newData, timestamp: { seconds: Date.now() / 1000 }, id: value.id }, ...prev]);
          //   @ts-ignore
          closeRef.current?.click?.();
        })
        .catch((err) => console.log(err))
        .finally(() => {
          setIsLoading(false);
        });
    } catch (err: any) {
      alert(err?.message ?? "Unknown error occurred");
      setIsLoading(false);
    }
  };

  return (
    <Dialog>
      <DialogTrigger>
        <Button>New Post</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New Post</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="w-full">
            <div className="grid grid-cols-2 gap-2">
              <FormField
                control={form.control}
                name="title"
                render={({ field }) => (
                  <FormItem className="col-span-2 sm:col-span-1">
                    <FormControl>
                      <Input placeholder="Title" {...field} value={field.value} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="body"
                render={({ field }) => (
                  <FormItem className="col-span-2 sm:col-span-1">
                    <FormControl>
                      <Input placeholder="Body" {...field} value={field.value} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="image"
                render={({ field: { onChange, value, ...rest } }) => (
                  <FormItem className="col-span-2">
                    <FormControl>
                      <Input
                        type="file"
                        {...rest}
                        onChange={(event) => {
                          const file = event?.target.files?.[0];
                          onChange(file);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            {!isLoading ? (
              <Button type="submit" className="mt-5">
                Create Post
              </Button>
            ) : (
              <Button disabled className="mt-5">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Posting
              </Button>
            )}
          </form>
        </Form>
        <DialogClose ref={closeRef} />
      </DialogContent>
    </Dialog>
  );
};

type Post = { body: string; title: string; imageURL: string; timestamp: { seconds: number } };
const postsRef = collection(db, "posts") as CollectionReference<Post>;

const postsQuery = query(postsRef, orderBy("timestamp", "desc"), limit(20));

const Posts2 = () => {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const [docs, setDocs] = useState<(Post & { id: string })[]>([]);
  const [mainCarouselApi, setMainCarouselApi] = useState<CarouselApi>();
  const [isRepairingImages, setIsRepairingImages] = useState(false);

  const fetchPosts = () => {
    getDocs(postsQuery).then((snapshot) => {
      const fetchedPosts = snapshot.docs.map((doc) => ({ ...doc.data(), id: doc.id }));
      setDocs(fetchedPosts);
    });
  };
  useEffect(() => {
    fetchPosts();
  }, []);

  useEffect(() => {
    docs.slice(0, 4).forEach((post) => preloadImage(post.imageURL));
  }, [docs]);

  const repairProblematicImages = async () => {
    const targetIndices = [2, 4].filter((index) => index < docs.length);

    if (targetIndices.length === 0) {
      toast({
        title: "No matching posts",
        description: <div>Need at least 5 posts to repair indices 3 and 5.</div>,
      });
      return;
    }

    setIsRepairingImages(true);

    try {
      const repairedById: Record<string, string> = {};

      for (const index of targetIndices) {
        const post = docs[index];
        let sourceBlob: Blob;

        try {
          // Avoid browser CORS issues by downloading through the Firebase Storage SDK.
          sourceBlob = await getBlob(ref(storage, post.imageURL));
        } catch {
          throw new Error(`Failed to download image for post ${post.id} from Firebase Storage.`);
        }

        const normalizedBlob = await normalizeImageBlob(sourceBlob);
        const storageRef = ref(storage, createStoragePath("post-images/repaired"));
        const uploadTask = await uploadBytes(storageRef, normalizedBlob, {
          contentType: NORMALIZED_IMAGE_TYPE,
          cacheControl: "public,max-age=31536000,immutable",
        });
        const repairedUrl = await getDownloadURL(uploadTask.ref);

        await updateDoc(doc(db, `posts/${post.id}`), {
          imageURL: repairedUrl,
        });

        repairedById[post.id] = repairedUrl;
      }

      setDocs((prevDocs) =>
        prevDocs.map((post) =>
          repairedById[post.id]
            ? {
                ...post,
                imageURL: repairedById[post.id],
              }
            : post,
        ),
      );

      toast({
        title: "Images repaired",
        description: <div>Reprocessed and updated post indices 3 and 5.</div>,
      });
    } catch (error: any) {
      toast({
        title: "Repair failed",
        description: <div>{error?.message ?? "Could not repair images."}</div>,
      });
    } finally {
      setIsRepairingImages(false);
    }
  };

  const [user] = useAuthState(auth);
  const loggedIn = !!user;

  return (
    <div className="w-full relative flex items-center justify-center">
      <Carousel
        opts={{
          align: "center",
          dragFree: false,
          containScroll: "trimSnaps",
          skipSnaps: false,
          loop: true,
        }}
        setApi={setMainCarouselApi}
        className="w-full"
      >
        <CarouselContent className="py-5">
          {docs.map((doc, index) => (
            <CarouselItem
              key={doc.id}
              className="basis-[60%] sm:basis-[53%] lg:basis-[41%] 2xl:basis-[27%] shrink-0 px-1 md:px-2 xl:px-3 noselect"
            >
              <PostItem
                {...doc}
                setDocs={setDocs}
                allPosts={docs}
                initialIndex={index}
                mainCarouselApi={mainCarouselApi}
              />
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="absolute -bottom-[6rem] left-10 flex items-center justify-center">
          <CarouselPrevious className="relative left-0 translate-x-0 hover:translate-x-0 w-14 h-14 [&>svg]:w-5 [&>svg]:h-5" />
        </div>
        <div className="absolute -bottom-[6rem] right-10 flex items-center justify-center">
          <CarouselNext className="relative right-0 translate-x-0 hover:translate-x-0 w-14 h-14 [&>svg]:w-5 [&>svg]:h-5" />
        </div>
      </Carousel>
      {/* // <div className="scroll-container relative flex justify-center"> */}
      {/* <div className="scroller" id="scroller" ref={scrollerRef}>
        <div className="min-w-[7vw]" id="first-spacer"></div>
        {docs.map((doc) => (
          <PostItem {...doc} key={doc.id} setDocs={setDocs} />
        ))}
        <div className="min-w-[7vw]" id="first-spacer"></div>
      </div>
      <div
        className="scroll-arrow"
        id="left-arrow"
        onClick={() => scrollerRef.current?.scrollBy({ left: -100, behavior: "smooth" })}
      >
        <i className="fa fa-arrow-left"></i>
      </div>
      <div
        className="scroll-arrow"
        id="right-arrow"
        onClick={() => scrollerRef.current?.scrollBy({ left: 100, behavior: "smooth" })}
      >
        <i className="fa fa-arrow-right"></i>
      </div> */}
      {loggedIn && (
        <div className="absolute -bottom-10 flex items-center gap-2">
          <NewPost setDocs={setDocs} />
          {/* {!isRepairingImages ? (
            <Button variant="outline" onClick={repairProblematicImages}>
              Repair images 3 and 5
            </Button>
          ) : (
            <Button variant="outline" disabled>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Repairing
            </Button>
          )} */}
        </div>
      )}
    </div>
  );
};

export default Posts2;
